import React, { useState, useEffect } from 'react';
import { Search, Filter, X, ChevronDown, ChevronUp, Target, Calendar, BookOpen } from 'lucide-react';

const AdvancedSearchFilters = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  semestreFilter,
  setSemestreFilter,
  anneeFilter,
  setAnneeFilter,
  showFilters,
  setShowFilters,
  onClearFilters,
  semestres,
  annees
}) => {
  const [activeFilters, setActiveFilters] = useState(0);

  // Compter les filtres actifs
  useEffect(() => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter && statusFilter !== 'tous') count++;
    if (semestreFilter) count++;
    if (anneeFilter) count++;
    setActiveFilters(count);
  }, [searchTerm, statusFilter, semestreFilter, anneeFilter]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('tous');
    setSemestreFilter('');
    setAnneeFilter('');
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recherche et Filtres Avancés</h3>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="h-4 w-4 mr-2" />
            {showFilters ? 'Masquer' : 'Afficher'} les filtres
            {activeFilters > 0 && (
              <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                {activeFilters}
              </span>
            )}
            {showFilters ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </button>
          
          {activeFilters > 0 && (
            <button
              onClick={clearAllFilters}
              className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 transition-colors border border-red-200 rounded-lg hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-2" />
              Effacer tout
            </button>
          )}
        </div>
      </div>

      {/* Barre de recherche principale */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un étudiant par nom, prénom ou matricule..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors placeholder-gray-400"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filtres avancés */}
      {showFilters && (
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtre par statut */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Target className="h-4 w-4 mr-2 text-purple-600" />
                Statut du Bulletin
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="tous">Tous les statuts</option>
                <option value="valide">Validés uniquement</option>
                <option value="en_attente">En attente uniquement</option>
              </select>
            </div>

            {/* Filtre par semestre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                Semestre
              </label>
              <select
                value={semestreFilter}
                onChange={(e) => setSemestreFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="">Tous les semestres</option>
                {semestres.map((semestre) => (
                  <option key={semestre.id} value={semestre.id}>
                    S{semestre.numero} - {semestre.libelle}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtre par année */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-green-600" />
                Année Académique
              </label>
              <select
                value={anneeFilter}
                onChange={(e) => setAnneeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-[#202654] transition-colors bg-white"
              >
                <option value="">Toutes les années</option>
                {annees.map((annee) => (
                  <option key={annee.id} value={annee.id}>
                    {annee.annee_debut}-{annee.annee_fin}
                    {annee.status && ' (Active)'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Résumé des filtres actifs */}
          {activeFilters > 0 && (
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>Filtres actifs:</span>
                {searchTerm && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                    Recherche: "{searchTerm}"
                  </span>
                )}
                {statusFilter && statusFilter !== 'tous' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                    Statut: {statusFilter === 'valide' ? 'Validés' : 'En attente'}
                  </span>
                )}
                {semestreFilter && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                    Semestre: {semestres.find(s => s.id === parseInt(semestreFilter))?.libelle || 'N/A'}
                  </span>
                )}
                {anneeFilter && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    Année: {annees.find(a => a.id === parseInt(anneeFilter))?.annee_debut}-{annees.find(a => a.id === parseInt(anneeFilter))?.annee_fin}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchFilters;
